import React from "react";
import { useSelector } from "react-redux";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { hasCurrentProviderCredentials } from "../utils/pspOnboarding";
import "../Styles/components/goLiveOutstandingDialog.scss";

const PROVIDER_META = {
	PAYSTACK: {
		labelKey: "api_settings.paystack_txt",
		route: "/payment_providers/paystack",
	},
	PAYSWITCH: {
		labelKey: "login.payswitch_txt",
		route: "/payment_providers/paySwitch",
	},
	APPSNMOBILE: {
		labelKey: "api_settings.OrchardPay_txt",
		route: "/payment_providers/appsNmobile",
	},
	JUNIPAY: {
		labelKey: "api_settings.Junipay",
		route: "/payment_providers/junipay",
	},
	ALPAY: {
		labelKey: "api_settings.alPay_txt",
		route: "/payment_providers/alpay",
	},
};

function SignatureIcon() {
	return (
		<svg width="20" height="16" viewBox="0 0 18 14" fill="none" aria-hidden="true">
			<path
				d="M1 10.5C2.5 6.5 4.2 3.2 6.1 3.5C8.2 3.8 8.5 9.2 10.2 9.5C12 9.8 13.2 5.2 14.8 4.8C15.8 4.55 16.6 6.2 17 7.5"
				stroke="currentColor"
				strokeWidth="1.6"
				strokeLinecap="round"
			/>
		</svg>
	);
}

function ProgressIcon() {
	return <i className="pi pi-sync" aria-hidden="true" />;
}

function KeyIcon() {
	return <i className="pi pi-key" aria-hidden="true" />;
}

/**
 * Restriction dialog for outstanding go-live steps.
 * Add Credentials and Signatory never show together: the backend only
 * generates an agreement after credentials are successfully saved.
 */
export default function GoLiveOutstandingDialog({ visible, onHide }) {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const business = useSelector((state) => state.businesses.business);
	const defMembership = useSelector((state) => state.defMembership.defMembership);
	const signingPending = useSelector((state) => state.documents.signingPending);
	const verifStatus = useSelector((state) => state.documents.verifStatus);
	const credentials = useSelector((state) => state.payswitch.credentials);
	const paymentCredentials = useSelector((state) => state.payswitch.paymentCredentials);
	const fetchingCredentials = useSelector((state) => state.payswitch.fetchingCredentials);
	const fetchingPaymentCredentials = useSelector((state) => state.payswitch.fetchingPaymentCredentials);

	const role = defMembership?.role;
	const canManage = role === "OWNER" || role === "ADMIN";
	const providerKey = business?.pmtProvider || "PAYSTACK";
	const providerMeta = PROVIDER_META[providerKey] || PROVIDER_META.PAYSTACK;
	const providerName = t(providerMeta.labelKey);
	const signerName = business?.signerFullName || t("idoc.signing_pending_signatory");
	const signerEmail = business?.signerEmail || "";
	const credentialsLoading = fetchingCredentials || fetchingPaymentCredentials;
	const credentialsSaved = hasCurrentProviderCredentials({
		credentials,
		paymentCredentials,
		provider: providerKey,
	});
	const hasSigner = Boolean(business?.signerFullName || business?.signerEmail);

	const showSignatoryCard = Boolean(credentialsSaved && signingPending && hasSigner);
	const needsCredentials = !credentialsSaved && !credentialsLoading;
	const needsVerification = Boolean(!business?.verified && verifStatus);
	const showProviderCard = Boolean(!showSignatoryCard && (needsCredentials || needsVerification));
	const showAddCredentialsCta = needsCredentials && canManage;

	const handleLinkApiKey = () => {
		onHide?.();
		navigate(providerMeta.route);
	};

	const note = showSignatoryCard
		? t("idoc.go_live_signatory_only_note")
		: canManage
			? t("idoc.go_live_admin_note", { provider: providerName })
			: t("idoc.go_live_non_admin_note");

	const footer = (
		<div className="go_live_dialog__actions">
			<Button
				label={t("common.ok")}
				className="go_live_dialog__btn-close"
				icon="pi pi-check"
				onClick={onHide}
				autoFocus
			/>
		</div>
	);

	return (
		<Dialog
			header={t("idoc.go_live_dialog_header")}
			visible={visible && (showSignatoryCard || showProviderCard)}
			style={{ width: "480px" }}
			onHide={onHide}
			footer={footer}
			className="go_live_dialog tier_dialog"
		>
			<p className="go_live_dialog__intro">{t("idoc.go_live_dialog_intro")}</p>

			{showSignatoryCard && (
				<div className="go_live_dialog__card">
					<div className="go_live_dialog__icon go_live_dialog__icon--signatory">
						<SignatureIcon />
					</div>
					<div className="go_live_dialog__card-body">
						<p className="go_live_dialog__card-title">{t("idoc.go_live_signatory_title")}</p>
						<p className="go_live_dialog__card-text">
							{t("idoc.go_live_signatory_text", {
								name: signerName,
								email: signerEmail,
							})}
						</p>
					</div>
				</div>
			)}

			{showProviderCard && (
				<div className="go_live_dialog__card">
					<div
						className={`go_live_dialog__icon ${
							showAddCredentialsCta ? "go_live_dialog__icon--key" : "go_live_dialog__icon--progress"
						}`}
					>
						{showAddCredentialsCta ? <KeyIcon /> : <ProgressIcon />}
					</div>
					<div className="go_live_dialog__card-body">
						<p className="go_live_dialog__card-title">
							{showAddCredentialsCta
								? t("idoc.go_live_api_key_title", { provider: providerName })
								: t("idoc.go_live_verif_title", { provider: providerName })}
						</p>
						<p className="go_live_dialog__card-text">
							{showAddCredentialsCta
								? t("idoc.go_live_api_key_text", { provider: providerName })
								: t("idoc.go_live_verif_text", { provider: providerName })}
						</p>
						{showAddCredentialsCta && (
							<div className="go_live_dialog__card-action">
								<Button
									label={t("idoc.go_live_link_api_key")}
									className="go_live_dialog__btn-link"
									onClick={handleLinkApiKey}
								/>
							</div>
						)}
					</div>
				</div>
			)}

			<p className="go_live_dialog__note">{note}</p>
		</Dialog>
	);
}
